<template>
  <LISAPopover
    v-model="popoverOpen"
    :disabled="disable || disabledStatusCircle"
    placement="bottom-start"
    open-on-hover
    :hover-close-delay="700"
  >
    <template #trigger="{ triggerAttrs }">
      <div
        v-bind="{ ...$attrs, ...triggerAttrs }"
        :class="{ 'lisaui:cursor-pointer': !disable && !disabledStatusCircle }"
        style="min-height: 28px; max-height: 28px; min-width: 28px; max-width: 28px"
        @click.stop
      >
        <LISASpinner v-if="loading" class="square24px" />
        <StatusCircleComponent
          v-else
          :status="
            [
              SQDCActionStatus.PLAN,
              SQDCActionStatus.DO,
              SQDCActionStatus.CHECK,
              SQDCActionStatus.ACT,
            ].indexOf(modelValue) + 1
          "
          :class="{ 'lisaui:cursor-pointer': !disable && !disabledStatusCircle }"
        />
      </div>
    </template>
    <template #default>
      <div class="taskStatusCircles">
        <slot :reset-timer="closePopover" :stop-timer="keepPopoverOpen">
          <div class="lisaui:flex lisaui:flex-col lisaui:gap-1">
            <div class="p-1" @click.stop="updateActionStatus(SQDCActionStatus.PLAN)">
              <StatusCircleComponent :status="1" style="height: 1.5rem" />
              {{ t("views.pdca.plan") }}
            </div>
            <div class="p-1" @click.stop="updateActionStatus(SQDCActionStatus.DO)">
              <StatusCircleComponent :status="2" style="height: 1.5rem" />
              {{ t("views.pdca.do") }}
            </div>
            <div class="p-1" @click.stop="updateActionStatus(SQDCActionStatus.CHECK)">
              <StatusCircleComponent :status="3" style="height: 1.5rem" />
              {{ t("views.pdca.check") }}
            </div>
            <div class="p-1" @click.stop="updateActionStatus(SQDCActionStatus.ACT)">
              <StatusCircleComponent :status="4" style="height: 1.5rem" />
              {{ t("views.pdca.act") }}
            </div>
            <template v-if="showActAndArchive">
              <hr class="w-100 my-0" />
              <div
                :class="{ disabled: disableActAndArchiveReason != null }"
                @click.stop="updateActionStatus(SQDCActionStatus.ACT, true)"
              >
                <InfoTooltip
                  :disabled="disableActAndArchiveReason == null"
                  :text="disableActAndArchiveReason"
                >
                  <template #activator>
                    <div
                      class="lisaui:flex lisaui:items-center lisaui:gap-2 p-1"
                      :style="`color: var(${disableActAndArchiveReason != null ? '--p-form-field-disabled-color' : '--p-info-color'})`"
                    >
                      <StatusCircleComponent
                        style="height: 1.5rem"
                        :status="4"
                        :filling-color="`var(${disableActAndArchiveReason != null ? '--p-form-field-disabled-color' : '--p-info-color'})`"
                      />
                      {{
                        showOnlyArchiveText ? t("views.pdca.archive") : t("views.pdca.actArchive")
                      }}
                    </div>
                  </template>
                </InfoTooltip>
              </div>
            </template>
          </div>
        </slot>
      </div>
    </template>
  </LISAPopover>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { inject, ref, watch, type PropType } from "vue";
import { LISAPopover, LISASpinner } from "@fi-solutions/lisa-ui";
import InfoTooltip from "@/components/shared/InfoTooltip.vue";
import { SQDCActionStatus } from "@/graphql";
import StatusCircleComponent from "@/components/event-action/task/StatusCircleComponent.vue";
import { DisabledStateInjectionKey } from "@/components/event-action/issue/IssueTypes";
import { computed } from "vue";

defineOptions({ inheritAttrs: false });

const props = defineProps({
  modelValue: {
    type: String as PropType<SQDCActionStatus>,
    required: false,
    default: SQDCActionStatus.PLAN,
  },
  loading: {
    type: Boolean,
    required: false,
    default: false,
  },
  disable: {
    type: Boolean,
    default: false,
  },
  showActAndArchive: {
    type: Boolean,
    default: false,
  },
  disableActAndArchiveReason: {
    type: String,
    default: undefined,
  },
  showOnlyArchiveText: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits({
  "update:modelValue": (val: SQDCActionStatus) => val,
  archiveEvent: (val: boolean) => val,
});
const disabledStatusCircleInternal = ref(false);
// is obsolet needs to be removed in the new version
const disabledStatusCircleIn = inject(DisabledStateInjectionKey, false);
const disabledStatusCircle = computed({
  get() {
    return disabledStatusCircleIn || disabledStatusCircleInternal.value;
  },
  set(val: boolean) {
    disabledStatusCircleInternal.value = val;
  },
});
// end: is obsolet needs to be removed in the new version
const popoverOpen = ref(false);
watch(
  () => props.disable || disabledStatusCircle.value,
  (disabled) => {
    if (disabled) popoverOpen.value = false;
  },
);
function closePopover(_delay?: number) {
  popoverOpen.value = false;
}
function keepPopoverOpen() {
  popoverOpen.value = true;
}
async function updateActionStatus(status: SQDCActionStatus, archive = false) {
  if (props.disable || disabledStatusCircle.value) return;
  if (archive && props.disableActAndArchiveReason) {
    return;
  }
  closePopover();
  if (archive) {
    emit("archiveEvent", true);
  } else {
    emit("update:modelValue", status);
  }
}

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.square24px {
  width: 100%;
  height: 100%;
}
.taskStatusCircles > div {
  padding-bottom: 0.25rem;
  > div {
    display: flex;
    gap: 0.25rem;
    align-items: center;
    &:hover {
      background: rgb(226 226 226);
      border-radius: 5px;
    }
    &:not(.disabled) {
      cursor: pointer;
    }
    &.disabled {
      background: var(--p-disabled-color) !important;
      border-radius: 5px;
      cursor: unset;
    }
  }
}
</style>
